/** Lógica de texto das respostas rápidas (docs/quick-replies.md), sem React: buscar, preencher e inserir. */

export interface QuickReplyItem {
  id: string;
  shortcut: string;
  body: string;
}

/** Minúsculas, sem acento e sem a barra do começo: assim "/Olá" encontra o atalho "ola". */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim().replace(/^\/+/, '');
}

/**
 * Filtra pelo que a pessoa digitou. Vêm primeiro os atalhos que COMEÇAM com a busca, depois os que a
 * contêm e por fim os que só têm a busca no texto; cada grupo na ordem alfabética do atalho. Busca vazia:
 * tudo, em ordem alfabética.
 */
export function filterQuickReplies<T extends QuickReplyItem>(
  items: readonly T[],
  query: string,
): T[] {
  const needle = normalizeSearch(query);
  const byShortcut = (a: T, b: T) => a.shortcut.localeCompare(b.shortcut);
  if (needle === '') return [...items].sort(byShortcut);
  const starts: T[] = [];
  const inShortcut: T[] = [];
  const inBody: T[] = [];
  for (const item of items) {
    const shortcut = normalizeSearch(item.shortcut);
    if (shortcut.startsWith(needle)) starts.push(item);
    else if (shortcut.includes(needle)) inShortcut.push(item);
    else if (normalizeSearch(item.body).includes(needle)) inBody.push(item);
  }
  return [...starts.sort(byShortcut), ...inShortcut.sort(byShortcut), ...inBody.sort(byShortcut)];
}

/** O nome só vale se for um nome de verdade (o contato sem nome guarda o telefone como nome). */
function realFirstName(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  if (!/\p{L}/u.test(trimmed)) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

/**
 * Troca `{nome}` (primeiro nome do cliente) e `{atendente}` (nome de quem atende). Cliente sem nome de
 * verdade: `{nome}` sai junto com os espaços que vêm antes dele ("Olá {nome}!" → "Olá!"). Outras chaves
 * ficam como estão. O que entra no lugar nunca é tratado como variável (nada de `{nome}` dentro do nome).
 */
export function fillVariables(
  body: string,
  who: { clientName?: string | null; agentName?: string | null },
): string {
  const first = realFirstName(who.clientName);
  const agent = (who.agentName ?? '').trim();
  return body.replace(/([ \t]*)\{(nome|atendente)\}/g, (_match, space: string, key: string) => {
    if (key === 'nome') return first === null ? '' : `${space}${first}`;
    return agent === '' ? '' : `${space}${agent}`;
  });
}

/**
 * Insere `text` no lugar da seleção. Se encosta em letra, ganha um espaço; o cursor fica no fim do que
 * foi inserido.
 */
export function insertAtSelection(
  value: string,
  start: number,
  end: number,
  text: string,
): { value: string; caret: number } {
  const from = Math.max(0, Math.min(start, end, value.length));
  const to = Math.min(value.length, Math.max(start, end));
  const before = value.slice(0, from);
  const after = value.slice(to);
  const lead = before !== '' && !/\s$/.test(before) ? ' ' : '';
  const trail = after !== '' && !/^\s/.test(after) ? ' ' : '';
  const inserted = `${lead}${text}${trail}`;
  return {
    value: `${before}${inserted}${after}`,
    caret: before.length + lead.length + text.length,
  };
}
